import React, { useState, useEffect, useMemo } from 'react';
import WebApp from '../../utils/WebAppPolyfill';
import { useTonWallet } from '@tonconnect/ui-react';
import './tabView.css';
import MineWalletSection from './MineWalletSection';
import BunsRewardPopup from '../BunsRewardPopup';
import BunsHistory from '../BunsHistory';
import { postFormData } from '../../network/request';
import { getBunsBalance, grantBuns } from '../../network/bunsApi';

const PANEL_KEYS = {
    ACCOUNT: 'account',
    PRIVACY: 'privacy',
    NOTIFICATIONS: 'notifications',
    HELP: 'help',
    FEEDBACK: 'feedback',
    ABOUT: 'about',
    BUNS: 'buns',
};

const PANEL_TITLES = {
    [PANEL_KEYS.ACCOUNT]: 'Account Settings',
    [PANEL_KEYS.PRIVACY]: 'Privacy & Security',
    [PANEL_KEYS.NOTIFICATIONS]: 'Notifications',
    [PANEL_KEYS.HELP]: 'Help Center',
    [PANEL_KEYS.FEEDBACK]: 'Send Feedback',
    [PANEL_KEYS.ABOUT]: 'About',
    [PANEL_KEYS.BUNS]: 'My BUNS',
};

const FAQ_ITEMS = [
    {
        q: 'How do I change my role (Job Seeker / Employer)?',
        a: 'Role is set when you first select your type. To change it, you may need to contact support or re-onboard if your app provides that flow.',
    },
    {
        q: 'How does the TON wallet work?',
        a: 'Open Profile → Wallet and connect with TonConnect. In Telegram, choose Telegram Wallet from the list to use the built-in wallet.',
    },
    {
        q: 'Where are my answers stored?',
        a: 'Question progress is kept in this session. For account-wide history, your backend would sync responses when you implement the API.',
    },
];

const STORAGE_PRIVACY = 'minePrivacyPrefs';
const STORAGE_NOTIF = 'mineNotifPrefs';
const STORAGE_NOTIF_BADGE = 'mineNotifBadgeDismissed';
const MINE_TON_WALLET_SNAPSHOT_KEY = 'mineTonWalletSnapshot';

function loadJson(key, fallback) {
    try {
        const s = localStorage.getItem(key);
        return s ? JSON.parse(s) : fallback;
    } catch {
        return fallback;
    }
}

function getInitUser() {
    const userInfo = localStorage.getItem('initUser');
    if (userInfo) {
        const userJson = JSON.parse(userInfo);
        const selectType = userJson.selectType || userJson.type || '1';
        const avatarUrl = userJson.photo_url || userJson.first_name || '';
        return {
            userName: userJson.user_name || 'User',
            userRoleType: selectType,
            userRole: selectType === '1' ? 'Job Seeker' : 'Employer',
            userAvatar: avatarUrl,
            userId: userJson.user_id,
            userEmail: userJson.email,
            chainKey: userJson.chain_key || '',
            publicKey: userJson.public_key || '',
            walletStatus: userJson.wallet_status || 'disconnected',
        };
    }
    return null;
}

export default function MineTabView() {
    const wallet = useTonWallet();
    const initUser = getInitUser();

    const [minePanel, setMinePanel] = useState(null);
    const [draftUserId, setDraftUserId] = useState('');
    const [draftEmail, setDraftEmail] = useState('');
    const [privacyAnalytics, setPrivacyAnalytics] = useState(true);
    const [privacyProfileVisible, setPrivacyProfileVisible] = useState(true);
    const [notifPush, setNotifPush] = useState(true);
    const [notifEmail, setNotifEmail] = useState(true);
    const [notifJobAlerts, setNotifJobAlerts] = useState(true);
    const [notifBadgeDismissed, setNotifBadgeDismissed] = useState(false);
    const [feedbackCategory, setFeedbackCategory] = useState('general');
    const [feedbackText, setFeedbackText] = useState('');
    const [expandedFaq, setExpandedFaq] = useState(null);
    const [serverWalletInfo, setServerWalletInfo] = useState({ chain_key: '', public_key: '', wallet_status: 'disconnected' });

    // BUNS state
    const [bunsBalance, setBunsBalance] = useState(0);
    const [rewardPopup, setRewardPopup] = useState({ show: false, amount: 0, type: '', detail: '', newBalance: 0 });

    // TON wallet info
    const walletInfo = useMemo(() => {
        if (!wallet) {
            return {
                chain: '',
                publicKey: '',
                address: '',
            };
        }
        const { chain, publicKey, address } = wallet.account;
        return { chain, publicKey, address };
    }, [wallet]);

    // Persist wallet info to localStorage
    useEffect(() => {
        const walletSnapshot = wallet
            ? {
                  chain_key: wallet.account.chain ?? '',
                  public_key: wallet.account.publicKey ?? '',
                  wallet_status: 'connected',
                  address: wallet.account.address ?? '',
                  wallet_name: wallet.name ?? '',
              }
            : {
                  chain_key: '',
                  public_key: '',
                  wallet_status: 'disconnected',
                  address: '',
                  wallet_name: '',
              };
        try {
            localStorage.setItem(MINE_TON_WALLET_SNAPSHOT_KEY, JSON.stringify(walletSnapshot));
        } catch {
            /* ignore quota / private mode */
        }
    }, [wallet]);

    useEffect(() => {
        loadPrefs();
        loadBunsBalance();
        // Read server-side wallet info from initUser
        if (initUser) {
            setServerWalletInfo({
                chain_key: initUser.chainKey || '',
                public_key: initUser.publicKey || '',
                wallet_status: initUser.walletStatus || 'disconnected',
            });
        }
    }, []);

    // Load BUNS balance
    async function loadBunsBalance() {
        if (!initUser?.userId) return;
        try {
            const res = await getBunsBalance(initUser.userId);
            if (res.success && res.data) {
                setBunsBalance(res.data.current_balance);
                // Sync to localStorage
                localStorage.setItem("bunsBalance", res.data.current_balance.toString());
                return;
            }
        } catch (e) {
            console.warn('Failed to load BUNS balance, using local data:', e);
        }
        // Fallback mode: use localStorage
        const localBuns = parseInt(localStorage.getItem("bunsBalance") || "0");
        setBunsBalance(localBuns);
    }

    // Refresh BUNS balance
    const refreshBunsBalance = loadBunsBalance;

    // Show reward popup
    const showReward = (amount, type, detail, newBal) => {
        setRewardPopup({ show: true, amount, type, detail, newBalance: newBal });
        setBunsBalance(newBal);
    };

    function loadPrefs() {
        const privacy = loadJson(STORAGE_PRIVACY, {});
        const notif = loadJson(STORAGE_NOTIF, {});
        const badgeDismissed = localStorage.getItem(STORAGE_NOTIF_BADGE) === '1';
        setPrivacyAnalytics(privacy.analytics !== false);
        setPrivacyProfileVisible(privacy.profileVisible !== false);
        setNotifPush(notif.push !== false);
        setNotifEmail(notif.email !== false);
        setNotifJobAlerts(notif.jobAlerts !== false);
        setNotifBadgeDismissed(badgeDismissed);
    }

    const userName = initUser?.userName || 'User';
    const userEmail = initUser?.userEmail || '';
    const userRole = initUser?.userRole || 'Job Seeker';
    const userRoleType = initUser?.userRoleType || '1';
    const userAvatar = initUser?.userAvatar || '';

    const getAvatarInitials = () => {
        const name = userName;
        return name.substring(0, 2).toUpperCase();
    };

    const isValidAvatarUrl = (url) => {
        if (!url || typeof url !== 'string') return false;
        const trimmed = url.trim();
        if (!trimmed) return false;
        try {
            new URL(trimmed);
            return true;
        } catch {
            return false;
        }
    };

    const handleLogout = () => {
        localStorage.clear();
        window.location.reload();
    };

    const openPanel = (key) => {
        if (key === PANEL_KEYS.ACCOUNT) {
            setMinePanel(key);
            setDraftUserId(userName);
            setDraftEmail(userEmail);
            return;
        }
        if (key === PANEL_KEYS.NOTIFICATIONS) {
            localStorage.setItem(STORAGE_NOTIF_BADGE, '1');
            setMinePanel(key);
            setNotifBadgeDismissed(true);
            return;
        }
        setMinePanel(key);
    };

    const closePanel = () => {
        setMinePanel(null);
        setExpandedFaq(null);
    };

    const savePrivacy = () => {
        localStorage.setItem(
            STORAGE_PRIVACY,
            JSON.stringify({
                analytics: privacyAnalytics,
                profileVisible: privacyProfileVisible,
            })
        );
        // toast('Privacy preferences saved');
    };

    const saveNotifications = () => {
        localStorage.setItem(
            STORAGE_NOTIF,
            JSON.stringify({
                push: notifPush,
                email: notifEmail,
                jobAlerts: notifJobAlerts,
            })
        );
        // toast('Notification preferences saved');
    };

    const saveAccount = () => {
        const raw = localStorage.getItem('initUser');
        const userJson = raw ? JSON.parse(raw) : {};
        const id = (draftUserId || '').trim() || userJson.user_id || 'User';
        const email = (draftEmail || '').trim() || userJson.email || '';
        userJson.user_id = id;
        userJson.email = email;
        localStorage.setItem('initUser', JSON.stringify(userJson));
        setMinePanel(null);
        toast('Account updated');
    };

    const toast = (message) => {
        if (WebApp?.showAlert) {
            WebApp.showAlert(message);
        } else {
            window.alert(message);
        }
    };

    const togglePrivacy = (field) => {
        if (field === 'privacyAnalytics') {
            setPrivacyAnalytics((prev) => !prev);
        } else if (field === 'privacyProfileVisible') {
            setPrivacyProfileVisible((prev) => !prev);
        }
    };

    const toggleNotif = (field) => {
        if (field === 'notifPush') {
            setNotifPush((prev) => !prev);
        } else if (field === 'notifEmail') {
            setNotifEmail((prev) => !prev);
        } else if (field === 'notifJobAlerts') {
            setNotifJobAlerts((prev) => !prev);
        }
    };

    const submitFeedback = () => {
        const text = (feedbackText || '').trim();
        if (!text) {
            toast('Please enter your feedback.');
            return;
        }
        const entry = {
            at: new Date().toISOString(),
            category: feedbackCategory,
            text,
        };
        const prev = loadJson('mineFeedbackLog', []);
        prev.push(entry);
        localStorage.setItem('mineFeedbackLog', JSON.stringify(prev.slice(-20)));
        setFeedbackText('');
        setMinePanel(null);
        toast('Thank you — your feedback was recorded locally.');
    };

    const openExternal = (url) => {
        try {
            WebApp.openLink(url);
        } catch {
            window.open(url, '_blank', 'noopener,noreferrer');
        }
    };

    const menuKeyDown = (e, key) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            openPanel(key);
        }
    };

    const confirmDataExport = () => {
        const run = () => {
            const payload = {
                profile: localStorage.getItem('initUser'),
                privacy: localStorage.getItem(STORAGE_PRIVACY),
                notifications: localStorage.getItem(STORAGE_NOTIF),
            };
            const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
            const a = document.createElement('a');
            a.href = URL.createObjectURL(blob);
            a.download = 'my-data-export.json';
            a.click();
            URL.revokeObjectURL(a.href);
            toast('Export downloaded');
        };
        if (WebApp.showConfirm) {
            WebApp.showConfirm('Download a JSON file with your saved app preferences?', (ok) => {
                if (ok) run();
            });
        } else if (window.confirm('Download a JSON file with your saved app preferences?')) {
            run();
        }
    };

    useEffect(() => {
        if (minePanel !== PANEL_KEYS.PRIVACY) return;
        savePrivacy();
    }, [privacyAnalytics, privacyProfileVisible]);

    useEffect(() => {
        saveNotifications();
    }, [notifPush, notifEmail, notifJobAlerts]);

    const renderToggleRow = (label, description, checked, onChange) => (
        <label className="mineToggleRow">
            <div className="mineToggleText">
                <span className="mineToggleLabel">{label}</span>
                {description ? <span className="mineToggleDesc">{description}</span> : null}
            </div>
            <span className="mineSwitch">
                <input type="checkbox" checked={checked} onChange={onChange} />
                <span className="mineSwitchSlider" aria-hidden />
            </span>
        </label>
    );

    const renderSubPanel = () => {
        if (!minePanel) return null;

        return (
            <div className="mineSubPanelOverlay" role="dialog" aria-modal="true" aria-labelledby="mineSubPanelTitle">
                <div className="mineSubPanel">
                    <header className="mineSubPanelHeader">
                        <button type="button" className="mineSubPanelBack" onClick={closePanel} aria-label="Back">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="22" height="22" aria-hidden>
                                <path d="M15 18l-6-6 6-6" />
                            </svg>
                        </button>
                        <h2 id="mineSubPanelTitle" className="mineSubPanelTitle">
                            {PANEL_TITLES[minePanel]}
                        </h2>
                        <span aria-hidden className="mineSubPanelHeaderPad" />
                    </header>
                    <div className="mineSubPanelBody">
                        {minePanel === PANEL_KEYS.ACCOUNT && (
                            <div className="mineFormCard">
                                <label className="mineField">
                                    <span className="mineFieldLabel">Display name</span>
                                    <input
                                        className="mineFieldInput"
                                        value={draftUserId}
                                        onChange={(e) => setDraftUserId(e.target.value)}
                                        placeholder="Your name"
                                        autoComplete="name"
                                    />
                                </label>
                                <label className="mineField">
                                    <span className="mineFieldLabel">Email</span>
                                    <input
                                        className="mineFieldInput"
                                        type="email"
                                        value={draftEmail}
                                        onChange={(e) => setDraftEmail(e.target.value)}
                                        placeholder="you@example.com"
                                        autoComplete="email"
                                    />
                                </label>
                                <p className="mineFieldHint">Role: {userRole} (set at signup)</p>
                                <button type="button" className="minePrimaryBtn" onClick={saveAccount}>
                                    Save changes
                                </button>
                            </div>
                        )}

                        {minePanel === PANEL_KEYS.PRIVACY && (
                            <div className="mineFormCard">
                                {renderToggleRow(
                                    'Usage analytics',
                                    'Help improve the app with anonymous usage data.',
                                    privacyAnalytics,
                                    () => togglePrivacy('privacyAnalytics')
                                )}
                                {renderToggleRow(
                                    'Profile visible to employers',
                                    'When off, your profile is hidden from recommendations.',
                                    privacyProfileVisible,
                                    () => togglePrivacy('privacyProfileVisible')
                                )}
                                <div className="mineDivider" />
                                <button type="button" className="mineSecondaryBtn" onClick={confirmDataExport}>
                                    Export my data
                                </button>
                                <p className="mineMuted">
                                    Downloads preferences stored in this app. Account deletion must be done through your organization or bot admin.
                                </p>
                            </div>
                        )}

                        {minePanel === PANEL_KEYS.NOTIFICATIONS && (
                            <div className="mineFormCard">
                                {renderToggleRow(
                                    'Push notifications',
                                    'Alerts inside Telegram when something important happens.',
                                    notifPush,
                                    () => toggleNotif('notifPush')
                                )}
                                {renderToggleRow(
                                    'Email updates',
                                    'Product news and tips (if email is on file).',
                                    notifEmail,
                                    () => toggleNotif('notifEmail')
                                )}
                                {renderToggleRow(
                                    'Job & talent alerts',
                                    'Matches for your role and searches.',
                                    notifJobAlerts,
                                    () => toggleNotif('notifJobAlerts')
                                )}
                            </div>
                        )}

                        {minePanel === PANEL_KEYS.HELP && (
                            <div className="mineHelpList">
                                {FAQ_ITEMS.map((item, i) => (
                                    <div key={i} className="mineFaqItem">
                                        <button
                                            type="button"
                                            className="mineFaqQ"
                                            onClick={() =>
                                                setExpandedFaq((prev) => (prev === i ? null : i))
                                            }
                                            aria-expanded={expandedFaq === i}
                                        >
                                            <span>{item.q}</span>
                                            <svg
                                                className={`mineFaqChevron ${expandedFaq === i ? 'open' : ''}`}
                                                viewBox="0 0 24 24"
                                                width="20"
                                                height="20"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2"
                                                aria-hidden
                                            >
                                                <path d="M6 9l6 6 6-6" />
                                            </svg>
                                        </button>
                                        {expandedFaq === i ? <p className="mineFaqA">{item.a}</p> : null}
                                    </div>
                                ))}
                                <button
                                    type="button"
                                    className="mineSecondaryBtn mineHelpLinkBtn"
                                    onClick={() => openExternal('https://telegram.org/faq')}
                                >
                                    Telegram FAQ (external)
                                </button>
                            </div>
                        )}

                        {minePanel === PANEL_KEYS.FEEDBACK && (
                            <div className="mineFormCard">
                                <label className="mineField">
                                    <span className="mineFieldLabel">Category</span>
                                    <select
                                        className="mineFieldInput mineSelect"
                                        value={feedbackCategory}
                                        onChange={(e) => setFeedbackCategory(e.target.value)}
                                    >
                                        <option value="general">General</option>
                                        <option value="bug">Bug report</option>
                                        <option value="feature">Feature idea</option>
                                    </select>
                                </label>
                                <label className="mineField">
                                    <span className="mineFieldLabel">Your message</span>
                                    <textarea
                                        className="mineFieldTextarea"
                                        rows={5}
                                        value={feedbackText}
                                        onChange={(e) => setFeedbackText(e.target.value)}
                                        placeholder="Tell us what went wrong or what you'd like to see…"
                                    />
                                </label>
                                <button type="button" className="minePrimaryBtn" onClick={submitFeedback}>
                                    Submit feedback
                                </button>
                                <p className="mineMuted">Saved on this device for demo; connect to your API to send to support.</p>
                            </div>
                        )}

                        {minePanel === PANEL_KEYS.ABOUT && (
                            <div className="mineFormCard mineAboutCard">
                                <p className="mineAboutName">BaoBounty</p>
                                <p className="mineAboutVer">Version 1.0.0</p>
                                <p className="mineMuted">Built with React and Telegram Web Apps.</p>
                                <div className="mineDivider" />
                                <button
                                    type="button"
                                    className="mineSecondaryBtn"
                                    onClick={() => openExternal('https://telegram.org/tos')}
                                >
                                    Terms of Service
                                </button>
                                <button
                                    type="button"
                                    className="mineSecondaryBtn"
                                    onClick={() => openExternal('https://telegram.org/privacy')}
                                >
                                    Privacy Policy
                                </button>
                            </div>
                        )}

                        {minePanel === PANEL_KEYS.BUNS && (
                            <BunsHistory />
                        )}
                    </div>
                </div>
            </div>
        );
    };

    const showNotifBadge = !notifBadgeDismissed;

    return (
        <div className="mineContainer mineTabRoot">
            <header className="mineHeader">
                <h1 className="mineHeaderTitle">Profile</h1>
                <div className="mineHeaderBtns">
                    <button type="button" className="mineHeaderBtn" aria-label="Settings" onClick={() => openPanel(PANEL_KEYS.ACCOUNT)}>
                        <svg className="mineHeaderIcon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                            <circle cx="12" cy="12" r="3" />
                            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
                        </svg>
                    </button>
                </div>
            </header>

            <div className="mineBody">
                <div className="mineProfileShell">
                    <div className="mineProfileCard">
                        <div className="profileSection">
                            {isValidAvatarUrl(userAvatar) ? (
                                <img
                                    className="profileAvatarImg"
                                    src={userAvatar}
                                    alt={userName}
                                    onError={(e) => {
                                        e.target.style.display = 'none';
                                        e.target.nextSibling.style.display = 'flex';
                                    }}
                                />
                            ) : null}
                            <div
                                className={`profileAvatar ${isValidAvatarUrl(userAvatar) ? 'profileAvatarHidden' : ''}`}
                                aria-hidden
                            >
                                {getAvatarInitials()}
                            </div>
                            <div className="profileInfo">
                                <p className="profileName">{userName}</p>
                                <p className="profileEmail">{userEmail}</p>
                                <span className="profileRole">{userRole}</span>
                            </div>
                        </div>
                    </div>
                </div>

                <MineWalletSection />

                {/* BUNS Balance */}
                <div className="menuSection">
                    <p className="menuGroupTitle">Rewards</p>
                    <div className="menuGroup">
                        <div
                            className="menuItem bunsMenuItem"
                            onClick={() => openPanel(PANEL_KEYS.BUNS)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => menuKeyDown(e, PANEL_KEYS.BUNS)}
                        >
                            <div className="menuIconWrap bunsIconWrap">
                                <span className="bunsMenuEmoji">🪙</span>
                            </div>
                            <span className="menuLabel">My BUNS</span>
                            <span className="menuBunsBalance">{bunsBalance}</span>
                            <svg className="menuArrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                                <path d="M9 6l6 6-6 6" />
                            </svg>
                        </div>
                    </div>
                </div>

                <div className="menuSection">
                    <p className="menuGroupTitle">Account</p>
                    <div className="menuGroup">
                        <div
                            className="menuItem"
                            onClick={() => openPanel(PANEL_KEYS.ACCOUNT)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => menuKeyDown(e, PANEL_KEYS.ACCOUNT)}
                        >
                            <span className="menuIconWrap">
                                <svg className="menuIcon" viewBox="0 0 24 24" fill="none" stroke="#2972FE" strokeWidth="2">
                                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                                    <circle cx="12" cy="7" r="4" />
                                </svg>
                            </span>
                            <span className="menuLabel">Account Settings</span>
                            <svg className="menuArrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                                <path d="M9 18l6-6-6-6" />
                            </svg>
                        </div>
                        <div
                            className="menuItem"
                            onClick={() => openPanel(PANEL_KEYS.PRIVACY)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => menuKeyDown(e, PANEL_KEYS.PRIVACY)}
                        >
                            <span className="menuIconWrap">
                                <svg className="menuIcon" viewBox="0 0 24 24" fill="none" stroke="#2972FE" strokeWidth="2">
                                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                                </svg>
                            </span>
                            <span className="menuLabel">Privacy & Security</span>
                            <svg className="menuArrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                                <path d="M9 18l6-6-6-6" />
                            </svg>
                        </div>
                        <div
                            className="menuItem"
                            onClick={() => openPanel(PANEL_KEYS.NOTIFICATIONS)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => menuKeyDown(e, PANEL_KEYS.NOTIFICATIONS)}
                        >
                            <span className="menuIconWrap">
                                <svg className="menuIcon" viewBox="0 0 24 24" fill="none" stroke="#2972FE" strokeWidth="2">
                                    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                                    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                                </svg>
                            </span>
                            <span className="menuLabel">Notifications</span>
                            {showNotifBadge ? <span className="menuBadge">3</span> : null}
                            <svg className="menuArrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                                <path d="M9 18l6-6-6-6" />
                            </svg>
                        </div>
                    </div>

                    <p className="menuGroupTitle">Support</p>
                    <div className="menuGroup">
                        <div
                            className="menuItem"
                            onClick={() => openPanel(PANEL_KEYS.HELP)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => menuKeyDown(e, PANEL_KEYS.HELP)}
                        >
                            <span className="menuIconWrap">
                                <svg className="menuIcon" viewBox="0 0 24 24" fill="none" stroke="#2972FE" strokeWidth="2">
                                    <circle cx="12" cy="12" r="10" />
                                    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                                    <line x1="12" y1="17" x2="12.01" y2="17" />
                                </svg>
                            </span>
                            <span className="menuLabel">Help Center</span>
                            <svg className="menuArrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                                <path d="M9 18l6-6-6-6" />
                            </svg>
                        </div>
                        <div
                            className="menuItem"
                            onClick={() => openPanel(PANEL_KEYS.FEEDBACK)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => menuKeyDown(e, PANEL_KEYS.FEEDBACK)}
                        >
                            <span className="menuIconWrap">
                                <svg className="menuIcon" viewBox="0 0 24 24" fill="none" stroke="#2972FE" strokeWidth="2">
                                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                                    <polyline points="15 3 21 3 21 9" />
                                    <line x1="10" y1="14" x2="21" y2="3" />
                                </svg>
                            </span>
                            <span className="menuLabel">Send Feedback</span>
                            <svg className="menuArrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                                <path d="M9 18l6-6-6-6" />
                            </svg>
                        </div>
                        <div
                            className="menuItem"
                            onClick={() => openPanel(PANEL_KEYS.ABOUT)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => menuKeyDown(e, PANEL_KEYS.ABOUT)}
                        >
                            <span className="menuIconWrap">
                                <svg className="menuIcon" viewBox="0 0 24 24" fill="none" stroke="#2972FE" strokeWidth="2">
                                    <circle cx="12" cy="12" r="10" />
                                    <line x1="12" y1="16" x2="12" y2="12" />
                                    <line x1="12" y1="8" x2="12.01" y2="8" />
                                </svg>
                            </span>
                            <span className="menuLabel">About</span>
                            <svg className="menuArrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                                <path d="M9 18l6-6-6-6" />
                            </svg>
                        </div>
                    </div>
                </div>

                {/* TON Wallet Info Display */}
                <div className="mineWalletInfoSection">
                    <p className="menuGroupTitle">TON Wallet Info</p>
                    <div className="mineFormCard">
                        <div className="mineWalletInfoRow">
                            <span className="mineWalletInfoKey">Chain:</span>
                            <span className="mineWalletInfoVal">{walletInfo.chain || serverWalletInfo.chain_key || 'Not connected'}</span>
                        </div>
                        <div className="mineWalletInfoRow">
                            <span className="mineWalletInfoKey">Public Key:</span>
                            <span className="mineWalletInfoVal mineWalletInfoValMono">{walletInfo.publicKey || serverWalletInfo.public_key || 'Not connected'}</span>
                        </div>
                        <div className="mineWalletInfoRow">
                            <span className="mineWalletInfoKey">Wallet Status:</span>
                            <span className="mineWalletInfoVal">{serverWalletInfo.wallet_status || 'disconnected'}</span>
                        </div>
                        <div className="mineWalletInfoRow">
                            <span className="mineWalletInfoKey">Address:</span>
                            <span className="mineWalletInfoVal mineWalletInfoValMono">{walletInfo.address || 'Not connected'}</span>
                        </div>
                    </div>
                </div>
            </div>

            {renderSubPanel()}

            {/* BUNS Reward Popup */}
            <BunsRewardPopup
                show={rewardPopup.show}
                bunsAmount={rewardPopup.amount}
                rewardType={rewardPopup.type}
                sourceDetail={rewardPopup.detail}
                newBalance={rewardPopup.newBalance}
                onClose={() => setRewardPopup((prev) => ({ ...prev, show: false }))}
            />
        </div>
    );
}
